/**
 * Value of the event wizard's file step (`_files`): the dropped files plus the
 * index of the one marked as the asset image (ASSET_INSTALL only). Kept as one
 * Formik value because TDAForm custom renderers only see their own field.
 */
export interface EventFilesValue {
    files: File[];
    /** Index into `files` of the image to set as the asset's node image, or null. */
    assetImageIndex: number | null;
}

export const EMPTY_EVENT_FILES: EventFilesValue = { files: [], assetImageIndex: null };

const validIndex = (index: unknown, length: number): number | null =>
    typeof index === 'number' && Number.isInteger(index) && index >= 0 && index < length ? index : null;

/** Accepts the legacy `File[]` shape, a full value, or nothing. */
export const normalizeEventFilesValue = (raw: unknown): EventFilesValue => {
    if (Array.isArray(raw)) {
        return { files: raw as File[], assetImageIndex: null };
    }
    if (raw && typeof raw === 'object' && Array.isArray((raw as any).files)) {
        const files = (raw as any).files as File[];
        return { files, assetImageIndex: validIndex((raw as any).assetImageIndex, files.length) };
    }
    return EMPTY_EVENT_FILES;
};

/** Replaces the file list (dropzone onChange). The marker survives while it still points at a file. */
export const setEventFiles = (value: EventFilesValue, files: File[]): EventFilesValue => ({
    files,
    assetImageIndex: validIndex(value.assetImageIndex, files.length),
});

/** Swaps one file in place (rename creates a new File object); the marker is unaffected. */
export const renameEventFile = (value: EventFilesValue, index: number, file: File): EventFilesValue => {
    if (index < 0 || index >= value.files.length) return value;
    const files = [...value.files];
    files[index] = file;
    return { ...value, files };
};

/** Removes one file; the marker follows its file (cleared if that file was removed). */
export const removeEventFile = (value: EventFilesValue, index: number): EventFilesValue => {
    const files = value.files.filter((_, i) => i !== index);
    let marker = value.assetImageIndex;
    if (marker !== null) {
        if (marker === index) marker = null;
        else if (marker > index) marker -= 1;
    }
    return { files, assetImageIndex: validIndex(marker, files.length) };
};

/** Exclusive toggle: marks `index`, or clears the marker when it already points there. */
export const toggleAssetImage = (value: EventFilesValue, index: number): EventFilesValue => ({
    ...value,
    assetImageIndex: value.assetImageIndex === index ? null : validIndex(index, value.files.length),
});
